import Vue from 'vue'
import Vuex from 'vuex'
import QuestionApi from '../services/QuestionApi'

Vue.use(Vuex);

export default new Vuex.Store({
    /** Global application state */
    state: {
        // current page
        current: 'intro',
        // question list
        questions: {},
        // selected answers
        answers: {},
        // array od previous pages
        previous: [],
        // error list
        errors: [],
        // selected maturity value
        selectedMaturity: 3,
        // maximum available readiness scores
        maxWeights: {
            total: 128,
            vehicle: 60,
            driver: 75,
            inf: 55,
            cost: 70
        },
        // host url
        url: null,
        // admin mode
        admin: false,
        // application readiness state
        ready: false,
        // is the page pre-loaded
        preload: false,
        // is the results page gated?
        gated: true,
        // is the results page gated?
        gatedLogged: false
    },
    /** Mutations ( monitored setters ) */
    mutations: {
        setCurrent: (state, current) => state.current = current,
        setAnswer: (state, answer) => {
            state.answers = {...state.answers, [state.current]: answer}
        },
        setAnswers: (state, answers) => {
            state.answers = answers
        },
        replacePrevious: (state, previous) => {
            state.previous = previous
        },
        setPrevious: (state, item) => state.previous.push(item),
        setQuestions: (state, questions) => state.questions = questions,
        addError: (state, error) => state.errors.push(error),
        resetErrors: (state) => state.errors = [],
        setSelectedMaturity: (state, maturity) => state.selectedMaturity = maturity,
        setCalculatedMaturity: (state, value) => state.calculatedMaturity = value,
        setUrl: (state, url) => state.url = url,
        setPreLoad: (state, preload) => state.preload = preload,
        setAdmin: (state, admin) => state.admin = admin,
        setReady: (state, ready) => state.ready = ready,
        setGated: (state, gated) => state.gated = gated,
        setGatedLogged: (state, logged) => state.gatedLogged = logged
    },
    /** Computed reactive getters */
    getters: {
        getNextQuestion: (state) => (answer) => {
            if (state.current === 'intro') {
                return 1;
            } else {
                // gated hijacking
                if (state.current === 'gate' && state.gated === false) {
                    return 'result';
                }
                // /gated high jacking

                let answerObjs = state.questions[state.current].answers.filter(obj => {
                    return obj.value === answer;
                });
                let next = answerObjs.pop().next;

                // gated hijacking
                // unless the gate has been filled in answers will be blocked...
                if (next === 'result' && state.gated === true) {
                    return 'gate';
                }
                // /gated hijacking

                if (!next) return 'contact';
                return (!isNaN(parseInt(next, 10))) ? parseInt(next, 10) : next;
            }
        },
        getCurrentQuestion: state => {
            return state.questions[state.current]
        },
        getAnswers: state => {
            return state.answers
        },
        getQuestion: state => id => {
            return state.questions[id]
        },
        getQuestions: state => {
            return state.questions
        },
        getMaxWeights: state => {
            return state.maxWeights
        },
        getUrl: state => {
            return state.url
        },
        getOverallReadiness: state => {
            let overall = 0;
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if(answer) {
                    overall += answer.logic.weights.total;
                }
            });
            return overall;
        },
        getVehicleReadiness: state => {
            let overall = 0;
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer) {
                    overall += answer.logic.weights.vehicle;
                }
            });
            return overall;
        },
        getDriverReadiness: state => {
            let overall = 0;
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer) {
                    overall += answer.logic.weights.driver;
                }
            });
            return overall;
        },
        getInfReadiness: state => {
            let overall = 0;
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer) {
                    overall += answer.logic.weights.inf;
                }
            });
            return overall;
        },
        getCostReadiness: state => {
            let overall = 0;
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer) {
                    overall += answer.logic.weights.cost;
                }
            });
            return overall;
        },
        getPositives: state => {
            let positives = [];
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer && answer.logic.positive) {
                    positives.push(answer.logic.positive);
                }
            });

            return positives;
        },
        getNegatives: state => {
            let negatives = [];
            Object.keys(state.answers).forEach((key) => {
                let answer = state.questions[key].answers.find(
                    item => {
                        return item.value === state.answers[key]
                    }
                );
                if (answer && answer.logic.negative) {
                    negatives.push(answer.logic.negative);
                }
            });

            return negatives;
        },
        getAnswerString: state => {
            return btoa(JSON.stringify(
                {
                        answers: state.answers,
                        maturity: state.selectedMaturity
                }));
        },
        getSelectedAnswer: state => {
            return (state.answers[state.current]) ? state.answers[state.current] : null;
        }
    },
    /** Actions ( logical monitored  mutations against state, can return promises ) */
    actions: {
        next: (context) => {
            // we don't want to store the gate as a previous set as it is a blocker
            if (context.state.current !== 'gate') {
                context.commit('setPrevious', context.state.current);
            }
            context.commit('setCurrent',
                context.getters.getNextQuestion(context.getters.getSelectedAnswer));
        },
        back: context => {
            context.commit('setCurrent', context.state.previous.pop());
        },
        loadQuestions: (context, isPreload) => {
            QuestionApi.getQuestions(
                (result) => {
                    context.commit('setPreLoad', isPreload);
                    context.commit('setQuestions', result);
                    if (isPreload) {
                        context.commit("setCurrent", "result");
                    }
                    context.commit("setReady", true);
                },
                (error) => {
                    context.commit('addError', error);
                }
            );
        },
        preLoadAnswers: (context, answers) => {
            context.commit("setAnswers", answers.answers);
            context.commit("setSelectedMaturity", answers.maturity);
        },
        restart: (context) => {
            context.commit("setCurrent", "intro");
            context.commit("replacePrevious", []);
            context.commit("setAnswers", {});
            context.commit("setSelectedMaturity", 3);
            context.commit("resetErrors");
            context.commit("setPreLoad", false);
        }
    }
});

